import { useState } from "react"
import { useTranslation } from "react-i18next"
import { useNavigate, useParams } from "react-router-dom"

import { BreadcrumbNav } from "@/components/breadcrumb-nav"
import { LoadingState } from "@/components/loading-state"
import { PageHeader } from "@/components/page-header"
import { Button } from "@/components/ui/button"
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import { AnalysePanel } from "@/features/transit/components/panels/analyse-panel"
import { CreatedPanel } from "@/features/transit/components/panels/created-panel"
import { EmbarquementPanel } from "@/features/transit/components/panels/embarquement-panel"
import { EmpotagePanel } from "@/features/transit/components/panels/empotage-panel"
import { FormulePanel } from "@/features/transit/components/panels/formule-panel"
import { RemisePanel } from "@/features/transit/components/panels/remise-panel"
import { TerminerPanel } from "@/features/transit/components/panels/terminer-panel"
import { DocumentsSidePanel } from "@/features/transit/components/documents-side-panel"
import { HeaderStats } from "@/features/transit/components/header-stats"
import { StageActionsRow } from "@/features/transit/components/stage-actions-row"
import { StageChip } from "@/features/transit/components/stage-chip"
import { Stepper } from "@/features/transit/components/stepper"
import { isStageUnlocked } from "@/features/transit/lib/stages"
import { useTransitOrder } from "@/features/transit/hooks/use-transit-orders"
import { paths } from "@/routes/paths"
import type { OT, OTStage } from "@/features/transit/api/types"

export function TransitDetailPage() {
  const { otId } = useParams<{ otId: string }>()
  const { t } = useTranslation(["transit", "common"])
  const navigate = useNavigate()

  const { data: ot, isLoading, isError } = useTransitOrder(otId)

  if (isLoading) {
    return (
      <div className="flex flex-col gap-6">
        <LoadingState />
      </div>
    )
  }

  if (isError || !ot) {
    return (
      <div className="flex flex-col gap-2">
        <h1 className="text-2xl font-semibold tracking-tight">
          {t("detail.notFound")}
        </h1>
        <Button
          variant="link"
          className="self-start px-0"
          onClick={() => navigate(paths.transitOrders)}
        >
          {t("detail.back")}
        </Button>
      </div>
    )
  }

  // Re-mount the body whenever the real OT stage changes so the local
  // `viewedStage` state seeds from the new current stage cleanly.
  return <DetailBody key={ot.stage} ot={ot} />
}

function DetailBody({ ot }: { ot: OT }) {
  const { t } = useTranslation(["transit", "common"])
  const [viewedStage, setViewedStage] = useState<OTStage>(ot.stage)

  // Footer "back" handler — pure panel selector, no API call.
  const onGoBack = (stage: OTStage) => {
    if (isStageUnlocked(ot.unlockedStages, stage)) setViewedStage(stage)
  }

  return (
    <div className="flex flex-col gap-5">
      <BreadcrumbNav
        crumbs={[
          { label: t("list.title"), to: paths.transitOrders },
          { label: ot.otNumber },
        ]}
      />

      <PageHeader
        eyebrow={
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="cursor-help">{ot.label || ot.otNumber}</span>
            </TooltipTrigger>
            <TooltipContent>{t("detail.tooltips.label")}</TooltipContent>
          </Tooltip>
        }
        title={
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="cursor-help">{ot.otNumber}</span>
            </TooltipTrigger>
            <TooltipContent>{t("detail.tooltips.otNumber")}</TooltipContent>
          </Tooltip>
        }
        description={
          <span className="inline-flex flex-wrap items-center gap-x-2 gap-y-1">
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="cursor-help">
                  {t("detail.client")} {ot.clientName}
                </span>
              </TooltipTrigger>
              <TooltipContent>{t("detail.tooltips.client")}</TooltipContent>
            </Tooltip>
            <span aria-hidden className="text-muted-foreground/40">
              ·
            </span>
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="cursor-help">
                  {t("detail.contract")}{" "}
                  {ot.contractNumber ?? `#${ot.contractId}`}
                </span>
              </TooltipTrigger>
              <TooltipContent>
                {t("detail.tooltips.contract")}
              </TooltipContent>
            </Tooltip>
          </span>
        }
        stretchActions
        meta={
          <div className="flex flex-wrap items-center gap-2">
            <StageChip stage={ot.stage} archived={ot.archived} />
          </div>
        }
        actions={
          <>
            <div className="flex justify-end">
              <DocumentsSidePanel ot={ot} />
            </div>
            <div className="mt-auto">
              <HeaderStats ot={ot} />
            </div>
          </>
        }
      />

      <Stepper ot={ot} viewedStage={viewedStage} onJump={setViewedStage} />

      {viewedStage === "CREATED" ? <CreatedPanel ot={ot} /> : null}
      {viewedStage === "ANALYSE" ? <AnalysePanel ot={ot} /> : null}
      {viewedStage === "FORMULE" ? <FormulePanel ot={ot} /> : null}
      {viewedStage === "EMPOTAGE" ? <EmpotagePanel ot={ot} /> : null}
      {viewedStage === "EMBARQUEMENT" ? <EmbarquementPanel ot={ot} /> : null}
      {viewedStage === "REMISE" ? <RemisePanel ot={ot} /> : null}
      {viewedStage === "TERMINER" ? <TerminerPanel ot={ot} /> : null}

      {/* Footer only acts on the OT's REAL stage. "Back" navigates the
          panel selector locally; Advance still mutates the server. */}
      <StageActionsRow ot={ot} onGoBack={onGoBack} />
    </div>
  )
}
